/**
 * ✏️ MANAGE VEHICLE PRICING DIALOG
 * Dialog for viewing, updating, and deleting airport pricing for a vehicle
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Stack,
  InputAdornment,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import { Flight, Room, Edit, Delete, Cancel, CheckCircle, Add } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import {
  airportPricingService,
  VehicleWithPricing,
  VehicleAirportPricing,
  UpdatePricingData,
} from '@/services/airportPricingService';
import SetAirportPricingDialog from './SetAirportPricingDialog';
import { Airport, airportService } from '@/services/airportService';

interface ManageVehiclePricingDialogProps {
  open: boolean;
  vehicle: VehicleWithPricing;
  onClose: () => void;
  onSuccess: () => void;
}

export const ManageVehiclePricingDialog: React.FC<ManageVehiclePricingDialogProps> = ({
  open,
  vehicle,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [pricings, setPricings] = useState<VehicleAirportPricing[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [editingPricing, setEditingPricing] = useState<string | null>(null);
  const [editData, setEditData] = useState<{
    pickupPrice: number;
    dropoffPrice: number;
    isAvailable: boolean;
    notes: string;
  }>({
    pickupPrice: 0,
    dropoffPrice: 0,
    isAvailable: true,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    if (open) {
      loadPricing();
      loadAirports();
    }
  }, [open, vehicle.vehicleId]);

  const loadPricing = async () => {
    setLoading(true);
    try {
      const response = await airportPricingService.getVehiclePricing(vehicle.vehicleId);
      if (response.success && response.pricings) {
        setPricings(response.pricings);
      } else {
        toast.error('Failed to load pricing');
      }
    } catch (error) {
      console.error('Error loading pricing:', error);
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const loadAirports = async () => {
    try {
      const response = await airportService.getAirports();
      if (response.success && response.airports) {
        setAirports(response.airports);
      }
    } catch (error) {
      console.error('Error loading airports:', error);
    }
  };

  const handleEdit = (pricing: VehicleAirportPricing) => {
    setEditingPricing(pricing.id);
    setEditData({
      pickupPrice: pricing.pickupPrice, // Backend returns Naira directly
      dropoffPrice: pricing.dropoffPrice,
      isAvailable: pricing.isAvailable,
      notes: pricing.notes || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingPricing(null);
    setEditData({
      pickupPrice: 0,
      dropoffPrice: 0,
      isAvailable: true,
      notes: '',
    });
  };

  const handleUpdate = async (pricingId: string) => {
    setSubmitting(true);
    try {
      const data: UpdatePricingData = {
        pickupPrice: Math.round(editData.pickupPrice * 100),
        dropoffPrice: Math.round(editData.dropoffPrice * 100),
        isAvailable: editData.isAvailable,
        notes: editData.notes.trim() || undefined,
      };

      const response = await airportPricingService.updatePricing(pricingId, data);
      if (response.success) {
        toast.success('Pricing updated successfully!');
        setEditingPricing(null);
        await loadPricing();
      } else {
        toast.error(response.error || 'Failed to update pricing');
      }
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error('Failed to update pricing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (pricingId: string) => {
    if (!confirm('Are you sure you want to delete this pricing? This action cannot be undone.')) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await airportPricingService.deletePricing(pricingId);
      if (response.success) {
        toast.success('Pricing deleted successfully!');
        await loadPricing();
      } else {
        toast.error(response.error || 'Failed to delete pricing');
      }
    } catch (error) {
      console.error('Error deleting pricing:', error);
      toast.error('Failed to delete pricing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (pricingId: string) => {
    setSubmitting(true);
    try {
      const response = await airportPricingService.deactivatePricing(pricingId);
      if (response.success) {
        toast.success('Pricing deactivated successfully!');
        await loadPricing();
      } else {
        toast.error(response.error || 'Failed to deactivate pricing');
      }
    } catch (error) {
      console.error('Error deactivating pricing:', error);
      toast.error('Failed to deactivate pricing');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    // Backend stores and returns prices in Naira (not kobo)
    return `₦${amount.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleAddSuccess = () => {
    setShowAddDialog(false);
    loadPricing();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Manage Airport Pricing</Typography>
              <Typography variant="caption" color="text.secondary">
                {vehicle.vehicleMake} {vehicle.vehicleModel} • {vehicle.plateNumber}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setShowAddDialog(true)}
              size="small"
            >
              Add Pricing
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : pricings.length === 0 ? (
            <Alert severity="info">
              No airport pricing configured for this vehicle yet. Click "Add Pricing" to set up pricing
              for different zones.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {pricings.map((pricing) => {
                const isEditing = editingPricing === pricing.id;

                return (
                  <Card key={pricing.id} variant="outlined">
                    <CardContent>
                      {/* Header */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ mb: 2 }}
                      >
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Flight fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight={600}>
                              {pricing.airport?.airportCode} - {pricing.airport?.airportName}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Room fontSize="small" color="action" />
                            <Typography variant="body2">
                              {pricing.zone?.zoneName}
                            </Typography>
                            <Chip label={pricing.zone?.distanceRange.display} size="small" />
                          </Stack>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {pricing.isAvailable ? (
                            <Chip
                              icon={<CheckCircle />}
                              label="Active"
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip icon={<Cancel />} label="Inactive" size="small" color="default" />
                          )}
                        </Stack>
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      {isEditing ? (
                        /* Edit Mode */
                        <Stack spacing={2}>
                          <TextField
                            label="Pickup Price"
                            type="number"
                            value={editData.pickupPrice || ''}
                            onChange={(e) =>
                              setEditData({ ...editData, pickupPrice: parseFloat(e.target.value) || 0 })
                            }
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                            }}
                            size="small"
                          />
                          <TextField
                            label="Dropoff Price"
                            type="number"
                            value={editData.dropoffPrice || ''}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                dropoffPrice: parseFloat(e.target.value) || 0,
                              })
                            }
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                            }}
                            size="small"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={editData.isAvailable}
                                onChange={(e) =>
                                  setEditData({ ...editData, isAvailable: e.target.checked })
                                }
                              />
                            }
                            label="Available for booking"
                          />
                          <TextField
                            label="Notes"
                            multiline
                            rows={2}
                            value={editData.notes}
                            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                            size="small"
                          />
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button onClick={handleCancelEdit} disabled={submitting} size="small">
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleUpdate(pricing.id)}
                              variant="contained"
                              disabled={submitting}
                              size="small"
                            >
                              {submitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        /* View Mode */
                        <>
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Pickup Price
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {formatCurrency(pricing.pickupPrice)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Dropoff Price
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {formatCurrency(pricing.dropoffPrice)}
                              </Typography>
                            </Box>
                            {pricing.notes && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Notes
                                </Typography>
                                <Typography variant="body2">{pricing.notes}</Typography>
                              </Box>
                            )}
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            sx={{ mt: 2 }}
                          >
                            <Tooltip title="Edit Pricing">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(pricing)}
                                disabled={submitting}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Pricing">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(pricing.id)}
                                disabled={submitting}
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Pricing Dialog */}
      {showAddDialog && (
        <SetAirportPricingDialog
          open={showAddDialog}
          vehicle={vehicle}
          airports={airports}
          onClose={() => setShowAddDialog(false)}
          onSuccess={handleAddSuccess}
        />
      )}
    </>
  );
};

export default ManageVehiclePricingDialog;
